import { Logger } from '../../utils';
import type { IpInfo } from './IpAddressService';

export interface RateLimitInfo {
  submissions: SubmissionAttempt[];
  isBlocked: boolean;
  nextAllowedTime?: number;
  remainingAttempts: number;
}

export interface SubmissionAttempt {
  timestamp: number;
  ip: string;
  deviceId: string;
  surveyId: string;
  success: boolean;
}

export interface RateLimitConfig {
  maxSubmissionsPerIP: number;
  maxSubmissionsPerDevice: number;
  timeWindowMs: number;
  blockDurationMs: number;
}

export class RateLimitService {
  private static readonly RATE_LIMIT_KEY = 'survey_rate_limit';
  private static readonly BLOCKED_IPS_KEY = 'blocked_ips';
  private static readonly BLOCKED_DEVICES_KEY = 'blocked_devices';

  // Default configuration - can be overridden
  private static readonly DEFAULT_CONFIG: RateLimitConfig = {
    maxSubmissionsPerIP: 3,           // Max 3 submissions per IP
    maxSubmissionsPerDevice: 2,       // Max 2 submissions per device
    timeWindowMs: 60 * 60 * 1000,     // Within 1 hour
    blockDurationMs: 24 * 60 * 60 * 1000  // Block for 24 hours
  };

  static checkRateLimit(
    ipInfo: IpInfo,
    deviceId: string,
    surveyId: string,
    config: Partial<RateLimitConfig> = {}
  ): RateLimitInfo {
    // Check if rate limiting is disabled via environment variable
    if (import.meta.env.VITE_DISABLE_RATE_LIMIT === 'true') {
      Logger.log('⚠️ Rate limiting is DISABLED');
      return {
        submissions: [],
        isBlocked: false,
        remainingAttempts: 999
      };
    }

    const effectiveConfig = { ...this.DEFAULT_CONFIG, ...config };
    const now = Date.now();

    // Clean up old entries first
    this.cleanupOldEntries(now, effectiveConfig.timeWindowMs);
    
    // Check if IP or device is currently blocked
    if (this.isIPBlocked(ipInfo.ip, now) || this.isDeviceBlocked(deviceId, now)) {
      return {
        submissions: this.getSubmissionHistory(ipInfo.ip, deviceId, surveyId),
        isBlocked: true,
        nextAllowedTime: this.getNextAllowedTime(ipInfo.ip, deviceId),
        remainingAttempts: 0
      };
    }

    // Get recent submissions for this IP and device
    const submissions = this.getRecentSubmissions(ipInfo.ip, deviceId, surveyId, now, effectiveConfig.timeWindowMs);
    
    // Count submissions by IP and device
    const ipSubmissions = submissions.filter(s => s.ip === ipInfo.ip && s.success);
    const deviceSubmissions = submissions.filter(s => s.deviceId === deviceId && s.success);
    
    // Check limits
    const ipLimitExceeded = ipSubmissions.length >= effectiveConfig.maxSubmissionsPerIP;
    const deviceLimitExceeded = deviceSubmissions.length >= effectiveConfig.maxSubmissionsPerDevice;
    
    const isBlocked = ipLimitExceeded || deviceLimitExceeded;
    
    if (isBlocked) {
      // Block the IP and/or device
      if (ipLimitExceeded) {
        this.blockIP(ipInfo.ip, now + effectiveConfig.blockDurationMs);
        Logger.log(`🚫 IP ${ipInfo.ip} blocked due to rate limit exceeded`);
      }
      if (deviceLimitExceeded) {
        this.blockDevice(deviceId, now + effectiveConfig.blockDurationMs);
        Logger.log(`🚫 Device ${deviceId} blocked due to rate limit exceeded`);
      }
    }

    const remainingIPAttempts = Math.max(0, effectiveConfig.maxSubmissionsPerIP - ipSubmissions.length);
    const remainingDeviceAttempts = Math.max(0, effectiveConfig.maxSubmissionsPerDevice - deviceSubmissions.length);
    const remainingAttempts = Math.min(remainingIPAttempts, remainingDeviceAttempts);

    return {
      submissions,
      isBlocked,
      nextAllowedTime: isBlocked ? now + effectiveConfig.blockDurationMs : undefined,
      remainingAttempts
    };
  }

  static recordSubmissionAttempt(
    ipInfo: IpInfo, 
    deviceId: string, 
    surveyId: string, 
    success: boolean
  ): void {
    const attempt: SubmissionAttempt = {
      timestamp: Date.now(),
      ip: ipInfo.ip,
      deviceId,
      surveyId,
      success
    };

    const history = this.getSubmissionHistory();
    history.push(attempt);

    // Keep only recent entries to prevent unbounded growth
    const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const filteredHistory = history.filter(h => h.timestamp > oneWeekAgo);

    this.saveSubmissionHistory(filteredHistory);
    
    Logger.log('📝 Recorded submission attempt:', {
      ip: ipInfo.ip,
      deviceId,
      surveyId,
      success,
      timestamp: new Date(attempt.timestamp).toISOString()
    });
  }

  private static getSubmissionHistory(ip?: string, deviceId?: string, surveyId?: string): SubmissionAttempt[] {
    try {
      const history = localStorage.getItem(this.RATE_LIMIT_KEY);
      const submissions: SubmissionAttempt[] = history ? JSON.parse(history) : [];
      
      if (!ip && !deviceId && !surveyId) {
        return submissions;
      }

      return submissions.filter(s => {
        if (ip && s.ip !== ip) return false;
        if (deviceId && s.deviceId !== deviceId) return false;
        if (surveyId && s.surveyId !== surveyId) return false;
        return true;
      });
    } catch {
      return [];
    }
  }

  private static saveSubmissionHistory(history: SubmissionAttempt[]): void {
    try {
      localStorage.setItem(this.RATE_LIMIT_KEY, JSON.stringify(history));
    } catch (error) {
      Logger.log('⚠️ Failed to save submission history:', error);
    }
  }

  private static getRecentSubmissions(
    ip: string,
    deviceId: string,
    surveyId: string,
    now: number,
    timeWindowMs: number
  ): SubmissionAttempt[] {
    const cutoff = now - timeWindowMs;
    const allSubmissions = this.getSubmissionHistory();
    
    return allSubmissions.filter(s => 
      s.timestamp > cutoff &&
      s.surveyId === surveyId &&
      (s.ip === ip || s.deviceId === deviceId)
    );
  }

  private static cleanupOldEntries(now: number, timeWindowMs: number): void {
    const cutoff = now - timeWindowMs * 2; // Keep entries for 2x the window for analysis
    const history = this.getSubmissionHistory();
    const cleaned = history.filter(h => h.timestamp > cutoff);
    
    if (cleaned.length !== history.length) {
      this.saveSubmissionHistory(cleaned);
      Logger.log(`🧹 Cleaned up ${history.length - cleaned.length} old submission entries`);
    }
  }

  private static isIPBlocked(ip: string, now: number): boolean {
    try {
      const blocked = localStorage.getItem(this.BLOCKED_IPS_KEY);
      if (!blocked) return false;
      
      const blockedIPs: Record<string, number> = JSON.parse(blocked);
      const blockUntil = blockedIPs[ip];
      
      if (!blockUntil) return false;
      
      if (now > blockUntil) {
        // Block expired, clean up
        delete blockedIPs[ip];
        localStorage.setItem(this.BLOCKED_IPS_KEY, JSON.stringify(blockedIPs));
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }

  private static isDeviceBlocked(deviceId: string, now: number): boolean {
    try {
      const blocked = localStorage.getItem(this.BLOCKED_DEVICES_KEY);
      if (!blocked) return false;
      
      const blockedDevices: Record<string, number> = JSON.parse(blocked);
      const blockUntil = blockedDevices[deviceId];
      
      if (!blockUntil) return false;
      
      if (now > blockUntil) {
        // Block expired, clean up
        delete blockedDevices[deviceId];
        localStorage.setItem(this.BLOCKED_DEVICES_KEY, JSON.stringify(blockedDevices));
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }

  private static blockIP(ip: string, until: number): void {
    try {
      const blocked = localStorage.getItem(this.BLOCKED_IPS_KEY);
      const blockedIPs: Record<string, number> = blocked ? JSON.parse(blocked) : {};
      blockedIPs[ip] = until;
      localStorage.setItem(this.BLOCKED_IPS_KEY, JSON.stringify(blockedIPs));
    } catch (error) {
      Logger.log('⚠️ Failed to block IP:', error);
    }
  }

  private static blockDevice(deviceId: string, until: number): void {
    try {
      const blocked = localStorage.getItem(this.BLOCKED_DEVICES_KEY);
      const blockedDevices: Record<string, number> = blocked ? JSON.parse(blocked) : {};
      blockedDevices[deviceId] = until;
      localStorage.setItem(this.BLOCKED_DEVICES_KEY, JSON.stringify(blockedDevices));
    } catch (error) {
      Logger.log('⚠️ Failed to block device:', error);
    }
  }

  private static getNextAllowedTime(ip: string, deviceId: string): number | undefined {
    const now = Date.now();
    let nextTime: number | undefined;

    try {
      const blockedIPs = localStorage.getItem(this.BLOCKED_IPS_KEY);
      if (blockedIPs) {
        const ips: Record<string, number> = JSON.parse(blockedIPs);
        if (ips[ip] && ips[ip] > now) {
          nextTime = ips[ip];
        }
      }

      const blockedDevices = localStorage.getItem(this.BLOCKED_DEVICES_KEY);
      if (blockedDevices) {
        const devices: Record<string, number> = JSON.parse(blockedDevices);
        if (devices[deviceId] && devices[deviceId] > now) {
          nextTime = Math.max(nextTime || 0, devices[deviceId]);
        }
      }
    } catch {
      // Ignore errors
    }

    return nextTime;
  }

  static clearRateLimitData(): void {
    try {
      localStorage.removeItem(this.RATE_LIMIT_KEY);
      localStorage.removeItem(this.BLOCKED_IPS_KEY);
      localStorage.removeItem(this.BLOCKED_DEVICES_KEY);
      Logger.log('🧹 Rate limit data cleared');
    } catch (error) {
      Logger.log('⚠️ Failed to clear rate limit data:', error);
    }
  }

  static getRateLimitStatus(ip: string, deviceId: string, surveyId: string): RateLimitInfo {
    const config = this.DEFAULT_CONFIG;
    return this.checkRateLimit({ ip, timestamp: Date.now(), source: 'cached' }, deviceId, surveyId, config);
  }
}